import { useEffect, useMemo, useState } from 'react';
import { Sparkles, Loader2, Trash2, Wand2, AlertCircle, ArrowUpRight, Compass, ShieldCheck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AdminAPI } from '@/lib/admin-api';
import { toast } from 'sonner';

const STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'promoted', label: 'Promoted' },
  { value: 'dismissed', label: 'Dismissed' },
];

function confidenceBadge(conf) {
  if (conf == null) return null;
  const pct = Math.round(conf * 100);
  const cls = pct >= 70 ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
    : pct >= 40 ? 'bg-amber-500/10 text-amber-600 border-amber-500/30'
    : 'bg-rose-500/10 text-rose-500 border-rose-500/30';
  return <Badge className={`${cls} text-[10px] font-mono-num`}>{pct}%</Badge>;
}

export default function AdminRoutingSuggestions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('new');
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // promote confirmation

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AdminAPI.routingSuggestions.list(status);
      setItems(data || []);
    } catch (e) {
      setError(e?.message || 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const classify = async (id) => {
    setBusyId(id);
    try {
      const updated = await AdminAPI.routingSuggestions.classify(id);
      setItems((prev) => prev.map((x) => (x.id === id ? updated : x)));
      toast.success(`Classified — ${Math.round((updated.confidence || 0) * 100)}% confidence`);
    } catch (e) {
      toast.error(e?.message || 'Classify failed');
    } finally {
      setBusyId(null);
    }
  };

  const promote = async (id) => {
    setBusyId(id);
    try {
      const hint = await AdminAPI.routingSuggestions.promote(id);
      toast.success(`Promoted to live routing hint: ${hint.merchant_name}`);
      setConfirmTarget(null);
      load();
    } catch (e) {
      toast.error(e?.message || 'Promote failed');
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (id) => {
    setBusyId(id);
    try {
      await AdminAPI.routingSuggestions.dismiss(id);
      toast.success('Suggestion dismissed');
      load();
    } catch (e) {
      toast.error(e?.message || 'Dismiss failed');
    } finally {
      setBusyId(null);
    }
  };

  const counts = useMemo(() => ({
    new: items.filter((i) => i.status === 'new').length,
    reviewed: items.filter((i) => i.status === 'reviewed').length,
    promoted: items.filter((i) => i.status === 'promoted').length,
    dismissed: items.filter((i) => i.status === 'dismissed').length,
  }), [items]);

  return (
    <div className="space-y-4" data-testid="admin-routing-suggestions-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">Intelligence</div>
          <h1 className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tracking-tight">Suggested routing hints</h1>
          <p className="mt-1 text-xs text-muted-foreground max-w-2xl">
            Unsupported hostnames the extension has observed cart totals on. Click <b>Classify</b> to draft a routing hint via GPT-5, review the proposed category/MCC, then <b>Promote</b> to publish. Promoted hints go live for all users immediately at <span className="font-mono-num">experimental</span> confidence.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px]"><Compass className="h-3 w-3 mr-1" />{items.length} in view</Badge>
        </div>
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList>
          {STATUSES.map((s) => (
            <TabsTrigger key={s.value} value={s.value} className="text-xs gap-1.5" data-testid={`admin-routing-suggestions-tab-${s.value}`}>
              {s.label}
              {counts[s.value] > 0 && status !== s.value && <Badge variant="outline" className="text-[9px] h-4 px-1">{counts[s.value]}</Badge>}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {error && (
        <Card className="p-3 border-rose-500/30 bg-rose-500/5 flex items-start gap-2 text-sm text-rose-700 dark:text-rose-300">
          <AlertCircle className="h-4 w-4 mt-0.5" /><span>{error}</span>
          <Button size="sm" variant="ghost" className="ml-auto" onClick={load}>Retry</Button>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <Table data-testid="admin-routing-suggestions-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[24%]">Hostname</TableHead>
              <TableHead className="hidden md:table-cell">Suggestion</TableHead>
              <TableHead className="hidden sm:table-cell">Observations</TableHead>
              <TableHead className="hidden sm:table-cell">Confidence</TableHead>
              <TableHead className="w-[220px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                  <Sparkles className="h-6 w-6 mx-auto opacity-50 mb-2" />
                  <div className="text-sm">No suggestions in <b>{status}</b>.</div>
                  <div className="text-[11px] mt-1">As the extension sees more carts on unsupported sites, they'll appear here.</div>
                </TableCell>
              </TableRow>
            ) : items.map((s) => (
              <TableRow key={s.id} className="h-12" data-testid={`admin-routing-suggestions-row-${s.id}`}>
                <TableCell className="py-2">
                  <div className="font-mono-num text-xs text-foreground">{s.hostname}</div>
                  <div className="text-[10px] text-muted-foreground">{new Date(s.created_at).toLocaleDateString('en-IN')} → {s.updated_at ? new Date(s.updated_at).toLocaleDateString('en-IN') : 'now'}</div>
                </TableCell>
                <TableCell className="py-2 hidden md:table-cell">
                  {s.suggested_name ? (
                    <div className="text-xs">
                      <div className="flex items-center gap-2"><b>{s.suggested_name}</b> {s.suggested_slug && <span className="font-mono-num text-muted-foreground">({s.suggested_slug})</span>}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {s.suggested_category}{s.suggested_mcc ? ` · MCC ${s.suggested_mcc}` : ''}
                        {s.suggested_color && (
                          <span className="inline-block ml-2 h-2.5 w-2.5 rounded-full align-middle border border-border" style={{ background: s.suggested_color }} />
                        )}
                      </div>
                      {s.rationale && <div className="mt-1 text-[10px] text-muted-foreground italic">&ldquo;{s.rationale}&rdquo;</div>}
                    </div>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">Not classified yet</span>
                  )}
                </TableCell>
                <TableCell className="py-2 hidden sm:table-cell">
                  <div className="text-xs font-mono-num">{s.observations}</div>
                  <div className="text-[10px] text-muted-foreground">{s.cart_extracted_count} cart{s.cart_extracted_count === 1 ? '' : 's'}</div>
                </TableCell>
                <TableCell className="py-2 hidden sm:table-cell">
                  {confidenceBadge(s.confidence)}
                </TableCell>
                <TableCell className="py-2 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    {s.status === 'new' && (
                      <Button size="sm" variant="outline" className="gap-1.5 h-7 text-[11px]" disabled={busyId === s.id} onClick={() => classify(s.id)} data-testid={`admin-routing-suggestions-classify-${s.id}`}>
                        {busyId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} Classify
                      </Button>
                    )}
                    {s.status === 'reviewed' && (
                      <Button size="sm" className="gap-1.5 h-7 text-[11px]" disabled={busyId === s.id} onClick={() => setConfirmTarget(s)} data-testid={`admin-routing-suggestions-promote-${s.id}`}>
                        <ArrowUpRight className="h-3 w-3" /> Promote
                      </Button>
                    )}
                    {s.status === 'promoted' && (
                      <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[10px] gap-1"><ShieldCheck className="h-3 w-3" /> Live</Badge>
                    )}
                    {['new', 'reviewed'].includes(s.status) && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" disabled={busyId === s.id} onClick={() => dismiss(s.id)} data-testid={`admin-routing-suggestions-dismiss-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-rose-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!confirmTarget} onOpenChange={(v) => !v && setConfirmTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish &ldquo;{confirmTarget?.suggested_name}&rdquo; as a routing hint?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-xs">
              <div>This will make the hint live for all users at <b>experimental</b> confidence. You can refine it in <b>Routing hints</b> right after.</div>
              {confirmTarget && (
                <div className="rounded-lg bg-[hsl(var(--surface-2))] border border-border p-3 mt-2">
                  <div><b>Slug:</b> <span className="font-mono-num">{confirmTarget.suggested_slug}</span></div>
                  <div><b>Hostname:</b> <span className="font-mono-num">{confirmTarget.hostname}</span></div>
                  <div><b>Category:</b> {confirmTarget.suggested_category}{confirmTarget.suggested_mcc ? ` · MCC ${confirmTarget.suggested_mcc}` : ''}</div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-routing-suggestions-promote-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmTarget && promote(confirmTarget.id)} data-testid="admin-routing-suggestions-promote-confirm">Publish</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
